/**
 * useForecast — Phase 05.1 Plan 05 Task 1; quick-261001-m3v five-quarter tabs.
 *
 * TanStack Query hook for GET /api/forecast?period=<key>, where the key is
 * CQ | NQ | FY or a quarter label ('FY2026-Q2').
 *
 * Config:
 *   - queryKey: ['forecast', period] — `period` participates in the cache key so
 *     switching tabs refetches the right period (and TanStack Query caches each
 *     period independently).
 *   - staleTime: 5 min — matches the cache refresh cadence (the sync-worker
 *     recompute cron; 5 min ensures we re-fetch before the agg cache is stale).
 *   - retry: 2 — standard for transient blips.
 *
 * The SF-OAuth exec read gate (D-19) is enforced server-side (Plan 04). A 401
 * from /api/forecast is handled by apiFetch (bounce to the SF login flow);
 * a 503 CACHE_NOT_READY surfaces as CacheNotReadyError → the amber cache-warming
 * banner (UX-07), exactly like the /main tiles.
 */
import { forecastQuarterWindow, type ForecastQuarterTab } from '@saledash/shared/forecast/quarters';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch, UnauthenticatedError } from '../lib/api-client.js';

import type { ForecastPeriodKey, ForecastResponse } from '@saledash/shared';

export function useForecast(
  period: ForecastPeriodKey,
): UseQueryResult<ForecastResponse> {
  return useQuery<ForecastResponse>({
    queryKey: ['forecast', period],
    queryFn: ({ signal }) =>
      // 05.3 D-16 — opt OUT of apiFetch's automatic 401 redirect. Without this
      // the navigation fires the moment the request comes back and the viewer
      // never sees anything, so there is nowhere to say that signing in through
      // Adeptia SSO is the way in. The error still throws; the page catches
      // UnauthenticatedError and renders ForecastAccessPanel instead.
      // This does NOT relax the gate — the server still refuses.
      apiFetch<ForecastResponse>(`/api/forecast?period=${encodeURIComponent(period)}`, {
        signal,
        noLoginRedirect: true,
      }),
    staleTime: 5 * 60_000,
    // Retrying a 401 twice just delays the access panel by two round trips —
    // an unauthenticated response is not a transient blip.
    retry: (failureCount, error) =>
      !(error instanceof UnauthenticatedError) && failureCount < 2,
  });
}

/** The five quarter payloads in view, keyed by their period label. */
export interface ForecastQuarterQueries {
  /** Q1..Q4 of the current fiscal year, then Q1 of the next — chronological. */
  window: ForecastQuarterTab[];
  byLabel: ReadonlyMap<string, UseQueryResult<ForecastResponse>>;
  /** The current quarter's payload (the workbook's first sheet, the YTD gross call). */
  current: UseQueryResult<ForecastResponse>;
  /** The next quarter's payload (the workbook's second sheet). */
  next: UseQueryResult<ForecastResponse>;
}

/**
 * quick-261001-m3v — ALL FIVE quarter tabs in hand at once (John, 2026-10-01:
 * "5 tabs on each page at all times"). Replaces useForecastAllPeriods (CQ/NQ/
 * FY): the tab bar is now the four quarters of the current fiscal year plus
 * Q1 of the next, closed quarters included, and the window is derived from
 * `now` so it rolls on 1 Jan by itself.
 *
 * WHY ALL FIVE, NOT JUST THE ONE ON SCREEN: D-14 puts BOTH current-and-next
 * quarter sheets in the exported workbook whichever tab the viewer is on, and
 * D-07/D-19 render the year-to-date box on every tab with the live gross call
 * of every open quarter as a column.
 *
 * FIVE CACHE ENTRIES, NOT FIVE REFETCHES PER RENDER — each goes through
 * `useForecast`, so the query key, stale time and retry policy are the
 * single-period hook's. The five calls are unconditional and in a fixed order
 * (the window always has exactly five entries), which keeps this a legal hook.
 */
export function useForecastQuarters(now: Date = new Date()): ForecastQuarterQueries {
  const window = forecastQuarterWindow(now);
  const [t0, t1, t2, t3, t4] = window as [
    ForecastQuarterTab,
    ForecastQuarterTab,
    ForecastQuarterTab,
    ForecastQuarterTab,
    ForecastQuarterTab,
  ];
  const q0 = useForecast(t0.label);
  const q1 = useForecast(t1.label);
  const q2 = useForecast(t2.label);
  const q3 = useForecast(t3.label);
  const q4 = useForecast(t4.label);
  const byLabel = new Map<string, UseQueryResult<ForecastResponse>>([
    [t0.label, q0],
    [t1.label, q1],
    [t2.label, q2],
    [t3.label, q3],
    [t4.label, q4],
  ]);
  const currentTab = window.find((t) => t.state === 'current') ?? t3;
  const currentIdx = window.indexOf(currentTab);
  const nextTab = window[currentIdx + 1] ?? t4;
  return {
    window,
    byLabel,
    current: byLabel.get(currentTab.label) ?? q3,
    next: byLabel.get(nextTab.label) ?? q4,
  };
}
